import { useLayoutEffect, useRef } from "react";
import { Archive, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { withAppTimeZone } from "@/lib/timezone";

/**
 * Formats an archived message timestamp (unix seconds or ISO string) into a readable time string.
 * @param {number|string|null|undefined} timestamp
 */
function formatArchivedTime(timestamp) {
  if (!timestamp) return "";
  try {
    // timestamp may be unix seconds (number) or ISO string
    const ms = typeof timestamp === "number" ? timestamp * 1000 : new Date(timestamp).getTime();
    if (Number.isNaN(ms)) return "";
    const d = new Date(ms);
    return d.toLocaleString(undefined, withAppTimeZone({
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }));
  } catch {
    return "";
  }
}

/**
 * Formats an archived message date string for display.
 * @param {string|null|undefined} dateStr
 */
function formatArchivedDate(dateStr) {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return dateStr;
    return d.toLocaleString(undefined, withAppTimeZone({
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }));
  } catch {
    return dateStr;
  }
}

/**
 * Renders a single archived message bubble.
 */
function ArchivedMessageBubble({ message }) {
  const fromMe = Boolean(message.from_me);
  const text = String(message.data || message.caption || "").trim();
  const timeLabel = formatArchivedTime(message.timestamp) || formatArchivedDate(message.message_date);
  const sender = String(message.sender || message.phone || "").trim();
  const chat_name = String(message.chat_name || "").trim();
  const hasMedia = Boolean(message.media);
  const mimeType = String(message.mime || "").toLowerCase();
  const isImage = hasMedia && (mimeType.startsWith("image/") || Boolean(message.thumb));
  const isSticker = Boolean(message.sticker);

  return (
    <div
      className={cn(
        "flex items-end gap-2",
        fromMe ? "justify-end" : "justify-start",
      )}
    >
      {!fromMe && (
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-500/15 text-[10px] font-semibold text-amber-700 ring-1 ring-amber-400/30 dark:bg-amber-500/20 dark:text-amber-300 dark:ring-amber-400/25">
          {chat_name.split(/\s+/).slice(0, 2).map((part) => part[0]?.toUpperCase() || "").join("")}
        </span>
      )}
      <article
        className={cn(
          "min-w-0 rounded-2xl border px-4 py-3 text-start",
          fromMe
            ? "border-amber-400/20 bg-amber-500/8 max-w-[85%] dark:border-amber-400/25 dark:bg-amber-500/12"
            : "border-amber-400/30 bg-amber-50/60 shadow-sm max-w-[75%] dark:border-amber-400/20 dark:bg-amber-950/20 dark:shadow-none",
        )}
      >
        {!fromMe && chat_name && (
          <p className="mb-1 max-w-48 truncate text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400">
            {chat_name}
          </p>
        )}
        {fromMe && sender && (
          <p className="mb-1 max-w-48 truncate text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400">
            {sender}
          </p>
        )}
        {isSticker && (
          <p className="text-xs italic text-muted-foreground">[Sticker]</p>
        )}
        {isImage && !text && (
          <p className="text-xs italic text-muted-foreground">[Image attachment]</p>
        )}
        {hasMedia && !isImage && !isSticker && !text && (
          <p className="text-xs italic text-muted-foreground">
            [{mimeType || "Media attachment"}]
          </p>
        )}

        {text ? (
          <p className="wrap-break-word text-sm leading-relaxed text-foreground">
            {text}
          </p>
        ) : null}
        <div className={cn("mt-1 whitespace-nowrap text-xs text-muted-foreground", fromMe ? "text-right" : "text-left")}>
          {timeLabel}
        </div>
      </article>
      {fromMe && (
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-500/15 text-[10px] font-semibold text-amber-700 ring-1 ring-amber-400/30 dark:bg-amber-500/20 dark:text-amber-300 dark:ring-amber-400/25">
          {sender.split(/\s+/).slice(0, 2).map((part) => part[0]?.toUpperCase() || "").join("")}
        </span>
      )}
    </div>
  );
}

/**
 * Archived messages section: separator, list of messages, load-more button.
 *
 * Implements messaging-app-style scroll anchoring: when older messages are
 * prepended at the top, the viewport stays pinned on the same visible message
 * instead of jumping to the top (same behaviour as iMessage / WhatsApp / Telegram).
 *
 * @param {object} props
 * @param {Array} props.archivedMessages
 * @param {boolean} props.archivedLoading
 * @param {string} props.archivedError
 * @param {boolean} props.archivedAllLoaded
 * @param {() => void} props.onLoadMore
 * @param {import("react").RefObject} props.scrollViewportRef - ref to the ScrollArea viewport element
 */
export function ArchivedMessageList({
  archivedMessages,
  archivedLoading,
  archivedError,
  archivedAllLoaded,
  onLoadMore,
  scrollViewportRef,
}) {
  const hasMessages = archivedMessages.length > 0;

  const prevScrollHeightRef = useRef(0);
  const loadCountRef = useRef(0);

  useLayoutEffect(() => {
    const viewport = scrollViewportRef?.current ?? null;
    if (!viewport) return;

    if (loadCountRef.current === 0) {
      loadCountRef.current = 1;
      return;
    }

    const newScrollHeight = viewport.scrollHeight;
    const delta = newScrollHeight - prevScrollHeightRef.current;

    if (delta > 0) {
      viewport.scrollTop += delta;
      prevScrollHeightRef.current = newScrollHeight;
    }
    loadCountRef.current += 1;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [archivedMessages.length]);

  useLayoutEffect(() => {
    const viewport = scrollViewportRef?.current ?? null;
    if (!viewport) return;
    prevScrollHeightRef.current = viewport.scrollHeight;

  }, [archivedMessages.length]);

  return (
    <div className="mb-4">
      {/* Top separator */}
      <div className="relative mb-4 flex items-center gap-3 py-1">
        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-400/50 to-amber-400/50" />
        <div className="flex shrink-0 items-center gap-1.5 rounded-full border border-amber-400/40 bg-amber-50/80 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-amber-700 shadow-sm dark:border-amber-400/30 dark:bg-amber-950/40 dark:text-amber-300">
          <Archive className="h-3 w-3" aria-hidden />
          <span>Archived messages</span>
        </div>
        <div className="h-px flex-1 bg-gradient-to-l from-transparent via-amber-400/50 to-amber-400/50" />
      </div>

      {/* Load more / spinner at the very top */}
      <div className="mb-3 flex justify-center">
        {archivedLoading ? (
          <div className="flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-50/70 px-4 py-2 text-xs text-amber-700 shadow-sm dark:border-amber-400/20 dark:bg-amber-950/30 dark:text-amber-300">
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            <span>Loading archived messages…</span>
          </div>
        ) : archivedError ? (
          <p className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
            {archivedError}
          </p>
        ) : !archivedAllLoaded && hasMessages ? (
          <button
            type="button"
            onClick={onLoadMore}
            className="flex items-center gap-1.5 rounded-full border border-amber-400/40 bg-amber-50/80 px-4 py-1.5 text-xs font-semibold text-amber-700 shadow-sm transition hover:bg-amber-100/80 hover:border-amber-400/60 dark:border-amber-400/30 dark:bg-amber-950/40 dark:text-amber-300 dark:hover:bg-amber-900/50"
          >
            <Archive className="h-3 w-3" aria-hidden />
            Load more
          </button>
        ) : archivedAllLoaded && hasMessages ? (
          <p className="text-[11px] text-muted-foreground">— Beginning of archive —</p>
        ) : null}
      </div>

      {/* Messages */}
      {hasMessages && (
        <div className="flex flex-col gap-3">
          {archivedMessages.map((msg, i) => (
            <ArchivedMessageBubble
              key={msg._id || msg.key_id || msg.message_id || i}
              message={msg}
            />
          ))}
        </div>
      )}

      <div className="relative mb-4 mt-4 flex items-center gap-3 py-1">
        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-400/50 to-amber-400/50" />
        <div className="flex shrink-0 items-center gap-1.5 rounded-full border border-amber-400/40 bg-amber-50/80 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-amber-700 shadow-sm dark:border-amber-400/30 dark:bg-amber-950/40 dark:text-amber-300">
          <Archive className="h-3 w-3" aria-hidden />
          <span>Archived messages</span>
        </div>
        <div className="h-px flex-1 bg-gradient-to-l from-transparent via-amber-400/50 to-amber-400/50" />
      </div>
      {!hasMessages && !archivedLoading && !archivedError && (
        <p className="py-4 text-center text-sm text-muted-foreground">
          No archived messages
        </p>
      )}
    </div>
  );
}
